import { BASE_URL } from "@/constants/common";
import {
  SocketEvents,
  TAISurveyResponse,
  TChatHistory,
  TClientToServerEvents,
  TConnectionLostResponse,
  TErrorResponse,
  TGetOperatorStatusResponse,
  TIdentificationCancelled,
  TIdentificationDismissed,
  TIdentificationRequest,
  TMessageResponse,
  TOperatorSurveyResponse,
  TSendMessagePayload,
  TServerToClientEvents,
  TShareContactResponse,
  TShareContacts,
  TSurveyFeedback,
} from "@/models/chat.model";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { io, Socket } from "socket.io-client";

type TMeta = {
  socket: Socket;
};

type SocketEventsHandlers = {
  onError?: (error: TErrorResponse) => void;
  onConnectionLost?: (data: TConnectionLostResponse, meta: TMeta) => void;

  onConnect?: (meta: TMeta) => void;
  onDisconnect?: (meta: TMeta) => void;

  onSendMessage?: (data: TSendMessagePayload, meta: TMeta) => void;
  onReceiveMessage?: (message: TMessageResponse, meta: TMeta) => void;

  onShareContactsResponse?: (data: TShareContactResponse, meta: TMeta) => void;

  onOperatorSurvey?: (data: TOperatorSurveyResponse, meta: TMeta) => void;
  onAISurvey?: (data: TAISurveyResponse, meta: TMeta) => void;

  onFetchHistoryRequest?: (meta: TMeta) => void;
  onFetchHistoryResponse?: (data: TChatHistory, meta: TMeta) => void;

  onStartNewSession?: () => void;
  onIdentificationRequest?: (data: TIdentificationRequest, meta: TMeta) => void;
  onIdentificationCancelled?: (data: TIdentificationCancelled, meta: TMeta) => void;
};

type UseSocketOptions = SocketEventsHandlers;

export const useSocketIO = (params: UseSocketOptions) => {
  const socketRef = useRef<Socket | null>(null);
  const eventsRef = useRef(params);

  useEffect(() => {
    eventsRef.current = params;
  }, [params]);

  useEffect(() => {
    if (socketRef.current) return;

    const socketInstance: Socket<TServerToClientEvents, TClientToServerEvents> =
      io(BASE_URL, {
        transports: ["websocket", "polling"],
        closeOnBeforeunload: true,
        tryAllTransports: true,
      });

    socketRef.current = socketInstance;

    const meta = {
      socket: socketInstance,
    };

    socketInstance.on(SocketEvents.CONNECT, () => {
      eventsRef.current.onConnect?.(meta);
    });

    socketInstance.on(SocketEvents.NEW_SESSION, () => {
      eventsRef.current.onStartNewSession?.();
    });

    socketInstance.on(SocketEvents.IDENTIFICATION_REQUEST, (data) => {
      eventsRef.current.onIdentificationRequest?.(data, meta);
    });

    socketInstance.on(SocketEvents.IDENTIFICATION_CANCELLED, (data) => {
      eventsRef.current.onIdentificationCancelled?.(data, meta);
    });

    socketInstance.on(SocketEvents.FETCH_HISTORY_RESPONSE, (data) => {
      eventsRef.current.onFetchHistoryResponse?.(data, meta);
    });

    socketInstance.on(SocketEvents.OPERATOR_SURVEY, (data) => {
      eventsRef.current.onOperatorSurvey?.(data, meta);
    });

    socketInstance.on(SocketEvents.DISCONNECT, () => {
      eventsRef.current.onDisconnect?.(meta);
    });

    socketInstance.on(SocketEvents.SHARE_CONTACT_RESPONSE, (data) => {
      eventsRef.current.onShareContactsResponse?.(data, meta);
    });

    socketInstance.on(SocketEvents.CONNECTION_LOST, (data) => {
      eventsRef.current.onConnectionLost?.(data, meta);
    });

    socketInstance.on(SocketEvents.AI_SURVEY, (data: TAISurveyResponse) => {
      eventsRef.current.onAISurvey?.(data, meta);
    });

    socketInstance.on(
      SocketEvents.RECEIVE_MESSAGE,
      (message: TMessageResponse) => {
        eventsRef.current.onReceiveMessage?.(message, meta);
      }
    );

    socketInstance.on(SocketEvents.ERROR, (error) => {
      eventsRef.current.onError?.(error);
    });

    return () => {
      socketInstance?.removeAllListeners();
      socketInstance?.disconnect();
      socketRef.current = null;
    };
  }, []);

  const handleConnect = useCallback(() => socketRef.current?.connect(), []);
  const handleDisconnect = useCallback(
    () => socketRef.current?.disconnect(),
    []
  );

  const handleSendMessage = useCallback((data: TSendMessagePayload) => {
    try {
      socketRef.current?.emit(SocketEvents.SEND_MESSAGE, data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleCheckConnection = useCallback(
    async (data: any, cb: (data: TGetOperatorStatusResponse) => void) => {
      try {
        const response = await socketRef.current?.emitWithAck(
          SocketEvents.OPERATOR_STATUS_REQUEST,
          data
        );

        cb(response);
      } catch (error) {
        console.error(error);
        cb({
          operatorMode: false,
        });
      }
    },
    []
  );

  const handleRate = useCallback(({ isAi, ...data }: TSurveyFeedback) => {
    try {
      socketRef.current?.emit(
        isAi
          ? SocketEvents.FEEDBACK_RESPONSE_AI
          : SocketEvents.FEEDBACK_RESPONSE_OPERATOR,
        data
      );
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleShareContacts = useCallback((data: TShareContacts) => {
    try {
      socketRef.current?.emit(SocketEvents.SHARE_CONTACTS, data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleIdentificationDismissed = useCallback((data: TIdentificationDismissed) => {
    try {
      socketRef.current?.emit(SocketEvents.IDENTIFICATION_DISMISSED, data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleFetchHistory = useCallback((userId: string, maxEntries = 100) => {
    socketRef.current?.emit(SocketEvents.FETCH_HISTORY, {
      userId,
      max_entries: maxEntries,
    });
  }, []);

  return useMemo(
    () => ({
      socket: socketRef.current,
      onConnect: handleConnect,
      onDisconnect: handleDisconnect,
      onSendMessage: handleSendMessage,
      onRate: handleRate,
      onCheckConnection: handleCheckConnection,
      onFetchHistory: handleFetchHistory,
      onShareContacts: handleShareContacts,
      onIdentificationDismissed: handleIdentificationDismissed,
    }),
    [
      handleConnect,
      handleDisconnect,
      handleSendMessage,
      handleRate,
      handleCheckConnection,
      handleFetchHistory,
      handleShareContacts,
      handleIdentificationDismissed,
    ]
  );
};
